<template>
    <div class="o-t-transport">
        <div class="o-t-transport__head">
            <OTTransportTypes
                @changeTransportType="changeTransportType"
            />
        </div>
        <div class="o-t-transport__schedule-wrapper">
            <OTTransportSelect
                v-if="!checkAviaType"
                :items="transportList"
                :activeTransportId="this.activeTransportItem ? this.activeTransportItem.routeId : 0"
                @changeTransportItem="changeTransportItem"
            />
            <div class="o-t-transport__schedule">
                <OAviaSchedule
                    v-if="checkAviaType"
                    :schedule="transportList"
                />
                <OTLandTransportSchedule
                    v-else-if="activeTransportItem && activeTransportItem.trans && activeTransportItem.trans.length && transportList.length"
                    :transports="activeTransportItem.trans"
                />
            </div>
        </div>
    </div>
</template>
<script>
import sortBy from 'lodash/sortBy';
import isEqual from 'lodash/isEqual';
import groupBy from 'lodash/groupBy';
import uniqWith from 'lodash/uniqWith';

import { mapGetters } from 'vuex'

import OAviaSchedule from '../../unique-organisms/o-avia-schedule';
import OTTransportTypes from '../../organisms/o-t-transport-types';
import OTTransportSelect from '../../organisms/o-t-transport-select';
import OTLandTransportSchedule from '../../organisms/o-t-land-transport-schedule';

import { PLACEHOLDERS } from "./constants";
import { TRANSPORT_TYPES } from "./constants";
import { TRANSPORT_API_MAP } from "@/utils/services/transport";
import { convertDataStructureToCommon } from "../../../utils/services/resrobot-service";

export default {
    name: 'OTTransport',
    components: {
        OAviaSchedule,
        OTTransportTypes,
        OTTransportSelect,
        OTLandTransportSchedule
    },
    data() {
        return {
            lastSync: '',
            transportList: [],
            activeTransportItem: null,
            activeTransportType: null,
            updateRransportListInterval: null,
        }
    },
    computed: {
        ...mapGetters([
            't',
            'getObjectsByCategory'
        ]),
        /**
         * @return {Array} - this is some like stations, or buss platform
         */
        transportTypeObjects() {
            return sortBy(this.getObjectsByCategory(+this.activeTransportType.id), ["distance"]) || [];
        },
        getTransportType() {
            if (this.t(this.activeTransportType, 'is_train')) return TRANSPORT_TYPES.TRAIN;
            if (this.t(this.activeTransportType, 'is_buss')) return TRANSPORT_TYPES.BUSS;
            if (this.t(this.activeTransportType, 'is_metro')) return TRANSPORT_TYPES.METRO;

            return TRANSPORT_TYPES.PLANE;
        },

        checkAviaType() {
            return this.getTransportType === TRANSPORT_TYPES.PLANE;
        }
    },
    watch: {
        async activeTransportType() {
            if(this.updateRransportListInterval) clearInterval(this.updateRransportListInterval)
            await this.prepareTransportList()
            this.updateRransportListInterval = setInterval(() => {
                this.prepareTransportList()
                this.changeTransportItem(this.transportList.find(transport => transport.routeId === this.activeTransportItem.routeId))
            }, 120000)
            this.changeTransportItem(this.transportList[0])
        }
    },
    methods: {
        changeTransportType(object) {
            this.activeTransportType = object
        },
        changeTransportItem(item) {
            this.activeTransportItem = {...item}
        },
        async prepareTransportList() {
            let prepareTransportList = [];
            await Promise.all(
                this.transportTypeObjects.map(async (object) => {
                    const objectTransports = await this.getObjectTransports(object)
                    prepareTransportList = [...prepareTransportList, ...objectTransports]
                })
            )

            if(!this.checkAviaType) {
                this.transportList = this.normalizeTransportList(prepareTransportList);
            } else {
                this.transportList = prepareTransportList
            }
        },
        /**
         * @param {Objeect} object - service item object, like buss stations
         * @return {Array} - transports who stay in this object
         */
        async getObjectTransports(object) {
            if(!this.getRuterStopId(object)) throw new Error(`Station code is not set for: ${this.getServiceName(object)}`);
            if(!this.getApiProvider(object)) throw new Error(`API provider is not set for: ${this.getServiceName(object)}`);
            let transports = [];

            if(this.getApiProvider(object) && TRANSPORT_API_MAP[this.getApiProvider(object)]){
                transports = this.minPlaceholders ? PLACEHOLDERS.slice(0, 2) : PLACEHOLDERS;
                try{
                    if (this.getApiProvider(object) === 'resrobot') {
                        const { Departure } = await TRANSPORT_API_MAP[this.getApiProvider(object)](this.getRuterStopId(object))
                        transports = this.prepareCommonDataStructure(Departure, object);
                    } else {
                        transports = await TRANSPORT_API_MAP[this.getApiProvider(object)](this.getRuterStopId(object), this.getTransportTypes(object))
                    }
                    this.lastSync = this.$moment()
                }
                catch(error){
                    throw new Error(`${this.apiProvider} data is not available: ${error}` )
                }
            }
            transports = transports.map(transport => ({...transport, parentObject: object}))
            return transports
        },
        getRuterStopId(object) {
            return this.t(object, 'ruter_stop_id') || null;
        },
        getServiceName(object) {
            return this.t(object, 'name') || '';
        },
        getApiProvider(object) {
            return this.t(object, 'timetables_api_provider') || null;
        },
        prepareCommonDataStructure(data, object) {
            return convertDataStructureToCommon(data, this.getTransportTypes(object))
        },
        getTransportTypes(object) {
            return this.t(object, 'ruter_transport_types') || null;
        },
        normalizeTransportList(list) {
            // get all transport
            let allTransport = list.reduce((acc, {number, trains, parentObject}) => [
                ...acc, ...trains.map(train => ({...train, number, parentObject}))
            ], [])
            // unique filter
            allTransport = uniqWith(allTransport, (oldEl, newEl) => {
                const oldEL = {...oldEl, parentObject: {}}
                const newEL = {...newEl, parentObject: {}}
                return isEqual(oldEL, newEL)
            })
            // group transport by buss-number
            let groupedTransport = groupBy(allTransport, 'routeId')
            // prepare data
            groupedTransport = Object.entries(groupedTransport).map(([routeId, trans]) => ({
                routeId,
                trans,
                parentObject: trans[0]?.parentObject,
                name: trans[0]?.name
            }))
            return groupedTransport
        }
    },
    beforeDestroy() {
        if(this.updateRransportListInterval) clearInterval(this.updateRransportListInterval)
    }
}
</script>

<style lang="scss" src="./theme.scss" />